import { computed } from 'vue'

import type { KanbanComment } from '@entities/kanban-comment'
import type { CommentLinkSegment } from '../../lib/comment-links'
import type { AppTextColor } from '@shared/ui/typography'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

import { useAdminRoutes } from '@shared/lib/routes'

import { TASKS_KANBAN_STAGE_LABEL } from '../../config/tasks-kanban-stage-label.const'
import { TASKS_KANBAN_STAGE_TEXT_COLOR } from '../../config/tasks-kanban-stage-text-color.const'
import { parseCommentLinks } from '../../lib/comment-links'

interface KanbanCommentItemOptions {
  props: { comment: KanbanComment }
  onEdit: () => void
}

interface KanbanCommentItemReturn {
  refAgentName: ComputedRef<string>
  refAgentUrl: ComputedRef<string>
  refDate: ComputedRef<string>
  refSegments: ComputedRef<CommentLinkSegment[]>
  refStatusLabel: ComputedRef<string>
  refStatusColor: ComputedRef<AppTextColor>
  handleClickEdit: () => void
}

export const useKanbanCommentItem = (options: KanbanCommentItemOptions): KanbanCommentItemReturn => {
  const refAgentName = computed<string>(() => options.props.comment.agent?.name ?? '(System)')

  const refAgentUrl = computed<string>(() => {
    if (!options.props.comment.agent) return ''
    return useAdminRoutes('admin.user.show', { user_id: options.props.comment.agent.id }) ?? ''
  })

  const refDate = computed<string>(() => dayjs(options.props.comment.updated_at, DateFormat.KanbanDate).format(DateFormat.KanbanDate))

  const refSegments = computed<CommentLinkSegment[]>(() => parseCommentLinks(options.props.comment.comment))

  const refStatusLabel = computed<string>(() => {
    const status = options.props.comment.kanban_task?.status
    return status ? TASKS_KANBAN_STAGE_LABEL[status] : ''
  })

  const refStatusColor = computed<AppTextColor>(() => {
    const status = options.props.comment.kanban_task?.status
    return status ? TASKS_KANBAN_STAGE_TEXT_COLOR[status] : 'slate-500'
  })

  const handleClickEdit = (): void => {
    options.onEdit()
  }

  return {
    refAgentName,
    refAgentUrl,
    refDate,
    refSegments,
    refStatusLabel,
    refStatusColor,
    handleClickEdit,
  }
}
